import { useState, useEffect } from 'react'
import { Button, Input, Label, Modal } from '../ui'
import { GeofenceMapEditor, Geofence } from './GeofenceMapEditor'

interface Location {
  id: string
  name: string
  locationType: 'physical' | 'remote'
  address: string
  coordinates?: { lat: number; lng: number }
  geofences: Geofence[]
  memberCount: number
  isAllMembers: boolean
  constraintSets: string[]
  isActive: boolean
  enforceGeofence?: boolean
}

interface Coordinates {
  lat: number
  lng: number
}

interface LocationModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (location: Omit<Location, 'id'>) => void
  location?: Location | null
  mode?: 'view' | 'edit' | 'add'
  onEdit?: () => void
}

// Helper function to calculate bounds for all geofences
function calculateGeofenceBounds(geofences: Geofence[]) {
  if (!geofences || geofences.length === 0) return null
  
  let minLat = Infinity, maxLat = -Infinity
  let minLng = Infinity, maxLng = -Infinity
  
  geofences.forEach(geofence => {
    if (geofence.type === 'CIRCLE' && geofence.center) {
      // For circles, consider the radius
      const radiusInDegrees = (geofence.radius || 100) / 111000 // Rough conversion
      minLat = Math.min(minLat, geofence.center.lat - radiusInDegrees)
      maxLat = Math.max(maxLat, geofence.center.lat + radiusInDegrees)
      minLng = Math.min(minLng, geofence.center.lng - radiusInDegrees)
      maxLng = Math.max(maxLng, geofence.center.lng + radiusInDegrees)
    } else if (geofence.type === 'POLYGON' && geofence.points) {
      geofence.points.forEach(point => {
        minLat = Math.min(minLat, point.lat)
        maxLat = Math.max(maxLat, point.lat)
        minLng = Math.min(minLng, point.lng)
        maxLng = Math.max(maxLng, point.lng)
      })
    }
  })
  
  return {
    center: {
      lat: (minLat + maxLat) / 2,
      lng: (minLng + maxLng) / 2
    },
    bounds: {
      north: maxLat,
      south: minLat,
      east: maxLng,
      west: minLng
    }
  }
}

export function LocationModal({ isOpen, onClose, onSave, location, mode = 'add', onEdit }: LocationModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    locationType: 'physical' as 'physical' | 'remote',
    address: '',
    coordinates: { lat: 37.7749, lng: -122.4194 }, // Default to San Francisco
    geofences: [] as Geofence[],
    isAllMembers: true,
    constraintSets: [] as string[],
    isActive: true,
    enforceGeofence: false
  })
  
  // Track map center separately from pin location
  const [mapCenter, setMapCenter] = useState<Coordinates>({ lat: 37.7749, lng: -122.4194 })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showMapEditor, setShowMapEditor] = useState(false)

  // Available constraint sets (in a real app, this would come from an API)
  const availableConstraintSets = [
    'Standard Hours',
    'Break Rules',
    'Warehouse Rules',
    'Client Site Rules',
    'Overtime Rules'
  ]

  useEffect(() => {
    if (location) {
      // Determine location type based on existing data
      const locationType = location.address || (location.geofences && location.geofences.length > 0) 
        ? 'physical' 
        : location.locationType || 'physical'
      
      // Ensure coordinates are valid numbers
      const coords = location.coordinates || { lat: 40.7128, lng: -74.0060 }
      const validCoords = {
        lat: typeof coords.lat === 'number' && !isNaN(coords.lat) ? coords.lat : 40.7128,
        lng: typeof coords.lng === 'number' && !isNaN(coords.lng) ? coords.lng : -74.0060
      }
      
      setFormData({
        name: location.name,
        locationType: locationType,
        address: location.address,
        coordinates: validCoords,
        geofences: location.geofences || [],
        isAllMembers: location.isAllMembers,
        constraintSets: location.constraintSets,
        isActive: location.isActive,
        enforceGeofence: location.enforceGeofence || false
      })
      
      // Show map for physical locations with geofences
      setShowMapEditor(locationType === 'physical')
      
      // Calculate the center to show all geofences
      if (location.geofences && location.geofences.length > 0) {
        const bounds = calculateGeofenceBounds(location.geofences)
        if (bounds) {
          setMapCenter(bounds.center)
        } else {
          setMapCenter(validCoords)
        }
      } else {
        setMapCenter(validCoords)
      }
    } else {
      // For new locations, show map by default
      setShowMapEditor(true)
      
      // Try to get user's current location first
      if (isOpen && 'geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords
            const coords = { lat: latitude, lng: longitude }
            setFormData({
              name: '',
              locationType: 'physical',
              address: '',
              coordinates: coords,
              geofences: [],
              isAllMembers: true,
              constraintSets: [],
              isActive: true,
              enforceGeofence: false
            })
            // Center map on user's location too
            setMapCenter(coords)
          },
          (error) => {
            console.log('Could not get current location, using NYC default:', error)
            // Fallback to NYC if geolocation fails
            const nycCoords = { lat: 40.7128, lng: -74.0060 }
            setFormData({
              name: '',
              locationType: 'physical',
              address: '',
              coordinates: nycCoords,
              geofences: [],
              isAllMembers: true,
              constraintSets: [],
              isActive: true,
              enforceGeofence: false
            })
            // Center map on NYC too
            setMapCenter(nycCoords)
          },
          {
            timeout: 10000, // 10 second timeout
            enableHighAccuracy: true
          }
        )
      } else {
        // Fallback to NYC if geolocation is not available
        const nycCoords = { lat: 40.7128, lng: -74.0060 }
        setFormData({
          name: '',
          locationType: 'physical',
          address: '',
          coordinates: nycCoords,
          geofences: [],
          isAllMembers: true,
          constraintSets: [],
          isActive: true,
          enforceGeofence: false
        })
        // Center map on NYC too
        setMapCenter(nycCoords)
      }
    }
    setErrors({})
  }, [location, isOpen])

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Location name is required'
    }

    if (formData.locationType === 'physical') {
      if (!formData.address.trim()) {
        newErrors.address = 'Address is required for physical locations'
      }

      // Only show geofence error if they try to save with no geofences
      if (formData.geofences.length === 0) {
        newErrors.geofences = 'At least one geofence is required for physical locations'
      }
      
      // If geofence enforcement is enabled but no geofences exist, show error
      if (formData.enforceGeofence && formData.geofences.length === 0) {
        newErrors.geofences = 'Cannot enforce geofence without defining at least one geofence'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    const locationData = {
      name: formData.name,
      locationType: formData.locationType,
      address: formData.address,
      coordinates: formData.coordinates,
      geofences: formData.geofences,
      memberCount: formData.isAllMembers ? 25 : 10, // Mock member count
      isAllMembers: formData.isAllMembers,
      constraintSets: formData.constraintSets,
      isActive: formData.isActive,
      enforceGeofence: formData.enforceGeofence
    }

    onSave(locationData)
  }

  const handleConstraintSetToggle = (constraintSet: string) => {
    setFormData(prev => ({
      ...prev,
      constraintSets: prev.constraintSets.includes(constraintSet)
        ? prev.constraintSets.filter(cs => cs !== constraintSet)
        : [...prev.constraintSets, constraintSet]
    }))
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {mode === 'view' ? 'Location Details' : location ? 'Edit Location' : 'Add New Location'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-900">Basic Information</h3>
            
            <div>
              <Label htmlFor="name">Location Name *</Label>
              {mode === 'view' ? (
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
                  {formData.name}
                </div>
              ) : (
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Main Office, Downtown Store, Construction Site A"
                  className={errors.name ? 'border-red-500' : ''}
                />
              )}
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>

            {/* Location Type */}
            <div>
              <Label className="block mb-3">Location Type *</Label>
              {mode === 'view' ? (
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                  <div className="flex items-center">
                    {formData.locationType === 'physical' ? (
                      <>
                        <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span className="font-medium">Physical Location</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                        </svg>
                        <span className="font-medium">Remote Work</span>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, locationType: 'physical' }))
                      setShowMapEditor(true)  // Show map when switching to physical
                    }}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      formData.locationType === 'physical'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center mb-2">
                      <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <span className="font-medium">Physical Location</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Office, store, or specific work site
                    </p>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        locationType: 'remote',
                        address: '',
                        geofences: [],
                        enforceGeofence: false
                      }))
                      setShowMapEditor(false)
                    }}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      formData.locationType === 'remote'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center mb-2">
                      <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                      </svg>
                      <span className="font-medium">Remote Work</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Work from home or anywhere
                    </p>
                  </button>
                </div>
              )}
            </div>

            {/* Address (only for physical locations) */}
            {formData.locationType === 'physical' && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label htmlFor="address">Address *</Label>
                  {mode !== 'view' && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if ('geolocation' in navigator) {
                          navigator.geolocation.getCurrentPosition(
                            (position) => {
                              const { latitude, longitude } = position.coords
                              setFormData(prev => ({ 
                                ...prev, 
                                coordinates: { lat: latitude, lng: longitude }
                              }))
                              // Optionally, reverse geocode to get address
                              // For now, just update the placeholder
                              const addressPlaceholder = `Near ${latitude.toFixed(4)}, ${longitude.toFixed(4)}`
                              setFormData(prev => ({ 
                                ...prev, 
                                address: prev.address || addressPlaceholder
                              }))
                            },
                            (error) => {
                              console.error('Error getting location:', error)
                              alert('Unable to get your location. Please check your browser permissions.')
                            }
                          )
                        } else {
                          alert('Geolocation is not supported by your browser')
                        }
                      }}
                      className="text-xs"
                    >
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Use Current Location
                    </Button>
                  )}
                </div>
                {mode === 'view' ? (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
                    {formData.address || 'No address provided'}
                  </div>
                ) : (
                  <Input
                    id="address"
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="123 Main St, City, State 12345"
                    className={errors.address ? 'border-red-500' : ''}
                  />
                )}
                {errors.address && <p className="mt-1 text-sm text-red-600">{errors.address}</p>}
              </div>
            )}
          </div>

          {/* Geofence Configuration - Only for physical locations */}
          {formData.locationType === 'physical' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-900">Geofence Configuration</h3>
                {mode !== 'view' && (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setShowMapEditor(!showMapEditor)}
                  >
                    {showMapEditor ? 'Hide Map' : 'Edit Geofences'}
                  </Button>
                )}
              </div>
              
              {errors.geofences && (
                <p className="text-sm text-red-600">{errors.geofences}</p>
              )}

              {/* Geofence Enforcement Checkbox */}
              {formData.geofences.length > 0 && (
                <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  {mode === 'view' ? (
                    <div className="flex items-center">
                      <svg className={`w-5 h-5 mr-2 ${formData.enforceGeofence ? 'text-green-600' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className={`font-medium ${formData.enforceGeofence ? 'text-blue-900' : 'text-gray-600'}`}>
                        Geofence enforcement is {formData.enforceGeofence ? 'active' : 'inactive'}
                      </span>
                    </div>
                  ) : (
                    <>
                      <input
                        id="enforceGeofence"
                        type="checkbox"
                        checked={formData.enforceGeofence}
                        onChange={(e) => setFormData(prev => ({ ...prev, enforceGeofence: e.target.checked }))}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                      />
                      <div className="flex-1">
                        <Label htmlFor="enforceGeofence" className="cursor-pointer font-medium text-blue-900">
                          Require being inside geofence to clock in
                        </Label>
                        <p className="text-sm text-blue-700 mt-1">
                          When enabled, team members can only clock in when they are physically within the defined geofence boundaries. 
                          This creates a team rule that enforces location-based attendance.
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}

              {(mode === 'view' || showMapEditor) ? (
                <GeofenceMapEditor
                  location={formData.coordinates}
                  initialCenter={mapCenter}
                  geofences={formData.geofences}
                  onGeofencesChange={mode === 'view' ? () => {} : (geofences) => {
                    setFormData(prev => ({ 
                      ...prev, 
                      geofences,
                      // Auto-disable enforcement if no geofences
                      enforceGeofence: geofences.length > 0 ? prev.enforceGeofence : false
                    }))
                  }}
                  onLocationChange={mode === 'view' ? undefined : (coordinates) => {
                    setFormData(prev => ({ ...prev, coordinates }))
                    // When pin is moved, update map center too
                    setMapCenter(coordinates)
                  }}
                  height="400px"
                />
              ) : (
                <div className="space-y-2">
                  {formData.geofences.length === 0 ? (
                    <div className="p-4 bg-gray-50 rounded-lg text-center">
                      <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <p className="text-sm text-gray-600 mb-2">No geofences defined</p>
                      <p className="text-xs text-gray-500">Click "Edit Geofences" to add location boundaries</p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-600 mb-2">
                        {formData.geofences.length} geofence{formData.geofences.length !== 1 ? 's' : ''} defined
                      </p>
                      <div className="space-y-1">
                        {formData.geofences.map((geofence) => (
                          <div key={geofence.id} className="flex items-center gap-2 text-sm text-gray-700">
                            <span className="font-medium">{geofence.name}</span>
                            <span className="text-gray-500">
                              ({geofence.type === 'CIRCLE' ? `${geofence.radius}m radius` : `${geofence.points?.length} points`})
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Team Assignment */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-900">Team Assignment</h3>
            
            {mode === 'view' ? (
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  formData.isAllMembers 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {formData.isAllMembers ? 'All Team Members' : 'Restricted Members Only'}
                </span>
              </div>
            ) : (
              <>
                <div className="flex items-center space-x-3">
                  <input
                    id="allMembers"
                    type="radio"
                    name="memberAssignment"
                    checked={formData.isAllMembers}
                    onChange={() => setFormData(prev => ({ ...prev, isAllMembers: true }))}
                    className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                  />
                  <Label htmlFor="allMembers" className="flex items-center cursor-pointer">
                    <span>All Team Members</span>
                    <span className="ml-2 text-sm text-gray-500">(Default)</span>
                  </Label>
                </div>

                <div className="flex items-center space-x-3">
                  <input
                    id="restrictedMembers"
                    type="radio"
                    name="memberAssignment"
                    checked={!formData.isAllMembers}
                    onChange={() => setFormData(prev => ({ ...prev, isAllMembers: false }))}
                    className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                  />
                  <Label htmlFor="restrictedMembers" className="flex items-center cursor-pointer">
                    <span>Restricted Members Only</span>
                  </Label>
                </div>

                {!formData.isAllMembers && (
                  <div className="ml-7 p-3 bg-gray-50 rounded-md">
                    <p className="text-sm text-gray-600 mb-2">Select specific team members who can access this location:</p>
                    <Button variant="secondary" size="sm">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Manage Members
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Constraint Sets */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-900">Constraint Sets</h3>
            {mode === 'view' ? (
              <div className="space-y-2">
                {formData.constraintSets.length > 0 ? (
                  formData.constraintSets.map((constraintSet) => (
                    <div key={constraintSet} className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                      <span className="text-sm font-medium text-gray-900">{constraintSet}</span>
                    </div>
                  ))
                ) : (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-500">
                    No constraint sets applied
                  </div>
                )}
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-600">
                  Select which rules and restrictions apply to this location:
                </p>
                
                <div className="space-y-2">
                  {availableConstraintSets.map((constraintSet) => (
                    <div key={constraintSet} className="flex items-center space-x-3">
                      <input
                        id={`constraint-${constraintSet}`}
                        type="checkbox"
                        checked={formData.constraintSets.includes(constraintSet)}
                        onChange={() => handleConstraintSetToggle(constraintSet)}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <Label htmlFor={`constraint-${constraintSet}`} className="cursor-pointer">
                        {constraintSet}
                      </Label>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Status */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-900">Status</h3>
            
            {mode === 'view' ? (
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  formData.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {formData.isActive ? 'Active' : 'Inactive'}
                </span>
                <span className="ml-2 text-sm text-gray-600">
                  {formData.isActive ? 'Available for check-ins' : 'Not available for check-ins'}
                </span>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <input
                  id="isActive"
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <Label htmlFor="isActive" className="cursor-pointer">
                  Location is active and available for check-ins
                </Label>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            {mode === 'view' ? (
              <>
                {onEdit && (
                  <Button type="button" onClick={onEdit}>
                    Edit Location
                  </Button>
                )}
                <Button type="button" variant="secondary" onClick={onClose}>
                  Close
                </Button>
              </>
            ) : (
              <>
                <Button type="button" variant="secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit">
                  {location ? 'Update Location' : 'Create Location'}
                </Button>
              </>
            )}
          </div>
        </form>
      </div>
    </Modal>
  )
}